import { isOpenException } from "../../../pages/shipments/Manual/Details/msrSummary";

// LWI-1644 — pure helpers for the exception UI. The API attaches `meta`
// (MsrExceptionCatalog: title, source, fields, hint) to every row.

export const isOpen = (row) => isOpenException(row);

export const SOURCE_LINE = "line";
export const SOURCE_PARTS_MASTER = "parts_master";
export const SOURCE_FLAG = "flag";
export const SOURCE_AUTO = "auto";

export const SOURCE_LABEL = {
  [SOURCE_LINE]: "On the part line",
  [SOURCE_PARTS_MASTER]: "Parts Manager",
  [SOURCE_FLAG]: "Trade compliance review",
  [SOURCE_AUTO]: "Set automatically",
};

export const sourceOf = (row) => row?.meta?.source || SOURCE_FLAG;
export const titleOf = (row) => row?.meta?.title || row?.exception_type || "—";
export const hintOf = (row) => row?.meta?.hint || "";

/** Frontend copy of MsrExceptionCatalog::PART_FIELD_LABELS (the API sends columns, not labels). */
export const PARTS_MASTER_LABELS = {
  ca_hs_code: "HS Code CA",
  us_hs_code: "HS Code US",
  ecl_goods: "ECL Goods",
  country_of_manufacture: "Country of Manufacture",
  usml_itar_technical_data: "USML Technology",
  usml_itar_articles: "USML Goods",
  eccn_ear_technology: "ECCN Technology",
  eccn_ear_items: "ECCN Goods",
  canadian_controlled_goods: "Canadian Controlled Goods",
  last_trade_compliance_review_date: "Last Trade Compliance Review Date",
  ecr_compliance_review_date: "ECR Compliance Review Date",
  schedule_b: "Schedule B",
};

const humanise = (col) =>
  String(col)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

export const partsMasterLabel = (col) =>
  PARTS_MASTER_LABELS[col] || humanise(col);

/** Parts Manager columns that can also be filled on the part line (drawer keys). */
export const PARTS_MASTER_TO_DRAWER = {
  ca_hs_code: "hsCodeCA",
  us_hs_code: "hsCodeUS",
};

/** Catalog `fields` of a row → the drawer keys to highlight (line source, plus the two HS codes). */
export const drawerFieldsFor = (row) => {
  const fields = row?.meta?.fields || [];
  const source = sourceOf(row);
  if (source === SOURCE_LINE) return fields;
  if (source === SOURCE_PARTS_MASTER) {
    // hs_code_exception_8710 says to correct one of the two; the HS code is
    // the only one the line can hold.
    return fields.map((f) => PARTS_MASTER_TO_DRAWER[f]).filter(Boolean);
  }
  return [];
};

export const partKey = (v) => String(v ?? "").trim().toLowerCase();

/** Rows grouped by part: Map(partKey → rows[]). */
export const groupByPart = (rows) => {
  const map = new Map();
  (Array.isArray(rows) ? rows : []).forEach((r) => {
    const k = partKey(r.part_id);
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(r);
  });
  return map;
};

/** Map(partKey → titles[]) of the open rows, for the grid icon and tooltip. */
export const openTitlesByPart = (rows) => {
  const map = new Map();
  (Array.isArray(rows) ? rows : []).filter(isOpen).forEach((r) => {
    const k = partKey(r.part_id);
    map.set(k, [...(map.get(k) || []), titleOf(r)]);
  });
  return map;
};

export const isManual = (row) => row?.resolved_value === "manual";

/** Part Manager page for an item number (the Parts dashboard links by item number). */
export const partPath = (item) => `/parts/${encodeURIComponent(item)}/details`;

// LWI-1756 - Re-check saves the parts section first, so the note says so.
export const CHECK_NOTE = "Unsaved part changes are saved first.";

/** Resolved By / Reason cell: who and why for a row resolved by hand, else "—". */
export const resolvedByReason = (row) => {
  if (!isManual(row)) return "—";
  const who = row.updated_by || "—";
  return row.comments ? `${who}: ${row.comments}` : who;
};

export const apiMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;
